import React, { useRef } from "react";

import ActorChannelView from "./ChannelView";

import Actor from "../models/Actor";
import Page from "../models/Page";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { ChannelType, channelTypeInfo } from "../models/ChannelType";
import { PagePrintInfo } from "../models/PagePrintInfo";
import ActorHeader from "./ActorViewHeader";
import Logger from "../Logger";
import { DragMode } from "../models/TreeviewModel";
import BaseModel from "../models/BaseModel";
import { showToast } from "../components/Toasts";
import { noItx } from "../translationItx/itx";
import { isUiModeUser } from "../uiMode";
import type Consumer from "../models/Consumer";

const logger = new Logger();

function DummyChannel(): any {
  return (
    <div
      style={{
        position: "relative",
        width: "50px",
        height: "5px",
        backgroundColor: "lightgreen",
        borderRadius: "2.5px",
      }}
    ></div>
  );
}
export default function ActorView(props: {
  actor: Actor;
  fixed: boolean;
  page: Page;
  print?: PagePrintInfo;
  showUsedOnly: boolean;
  showUsedChannels: boolean;
}) {
  const { actor, page, fixed, print, showUsedOnly, showUsedChannels } = props;

  const actorElement = useRef<HTMLDivElement>(null);

  useOnChangeWatcher(state.uiMode);

  useOnChangeWatcher(state.selectionManager);

  useOnChangeWatcher(actor, (changedProperty, changedObject) => {
    console.log("update actor", changedProperty);
    page.updateConnections();
    return true;
  });

  const headerHeight = 60;

  var i = 0;
  const children: any = [];

  var offset = headerHeight;

  const maxWidth = 50;

  if (actor.selected || showUsedChannels) {
    let sortedChannels = actor.channels.sortBy("tag");

    if (!actor.template && sortedChannels.length == 0) {
      children.push(DummyChannel());
    }

    if (isUiModeUser()) {
      const consumer = state.selectionManager.selectedObject as Consumer;
      if (consumer?.isConsumer) {
        const channel = consumer.getConnectedChannel();
        if (channel && channel.parent === actor) {
          sortedChannels = [channel];
        } else {
          return null;
        }
      }
    } else {
      if (showUsedChannels && !actor.selected && state.selectionManager.selectedObject?.parent != actor) {
        sortedChannels = sortedChannels.filter((channel) => channel.hasConnectionOnPage(page, false));
      }
    }

    sortedChannels.forEach((channel) => {
      if (
        channel.channelType != ChannelType.General &&
        channel.channelType != ChannelType.Unknown &&
        channel.channelType != ChannelType.Environment &&
        channel.channelType != ChannelType.TemperatureMeasurement
      ) {
        if (!channel.active && !actor.selected) {
          return;
        }

        children.push(
          <ActorChannelView
            maxWidth={maxWidth}
            page={page}
            fixed={fixed}
            key={channel.id}
            channel={channel}
            print={print}
            showAllChannels={showUsedChannels}
          />,
        );

        offset += 30;
      }
    });
  } else {
    let channel = actor.channels.findFirst((channel) => channel.connections.size == 0);

    if (channel || !actor.template) {
      children.push(DummyChannel());
    }
  }

  function onContextMenu(ev: any) {
    state.contextMenuManager.show(ev, actor);
  }

  async function handleDragEnter(mode: DragMode, sData: string) {
    try {
      const data = JSON.parse(sData);

      if (data.id) {
        const obj = state.objectRegistry.getObject(data.id) as BaseModel;
        if (obj) {
          switch (obj.className) {
            case noItx("Consumer"):
              actorElement.current?.classList.add("zoomOnDragOver"); // no itx
              break;
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }
  }

  async function handleDrop(mode: DragMode, sData: string) {
    let txt = state.translate("treeViewModel.dragADropforbidden", `Drag&Drop ist nicht erlaubt`);
    try {
      const data = JSON.parse(sData);

      if (data.id) {
        const obj = state.objectRegistry.getObject(data.id) as BaseModel;
        if (obj) {
          const res = await actor.treeViewModel.handleDrop2(mode, obj);
          if (res.errorMessage) {
            txt += ":\n" + res.errorMessage;
          }
          if (res.success) {
            if (res.errorMessage) {
              showToast({
                isError: false,
                text: res.errorMessage,
                time: 5000,
              });
            }
            return;
          }
        }
      }
    } catch (ex) {
      logger.error(ex);
    }

    showToast({
      isError: true,
      text: txt,
    });
  }

  function onDragEnter(e: any) {
    try {
      if (state.dragData) {
        const mode = e.dataTransfer.effectAllowed;
        handleDragEnter(mode, state.dragData);
        return;
      }
    } catch (ex) {
      logger.error(ex);
    }
  }
  function onDragLeave(e: any) {
    actorElement.current?.classList.remove("zoomOnDragOver"); // no itx
  }

  function onDrop(e: any) {
    try {
      var items = e.dataTransfer.items;
      const item = items[0];
      if (item.kind === "string") {
        item.getAsString(function (sData: string) {
          const mode = e.dataTransfer.effectAllowed;
          handleDrop(mode, sData);
        });
      }

      actorElement.current?.classList.remove("zoomOnDragOver"); // no itx
    } catch (ex) {
      logger.error(ex);
    }

    e.preventDefault();
  }
  const isSupply = channelTypeInfo(actor.channelType).isSupply;
  function getActorClassName(): string {
    let className = "actor"; // no itx
    if (print) {
      className = "actorPrint"; // no itx
    } else if (actor.selected) {
      // actor menu was vissable because of class Name :"actor seleced menu"
      if (state.activePaneId == page.id) {
        className = className + " selected"; // no itx
      }
    }
    return className;
  }

  return (
    <div
      id={actor.id}
      className={actor.selected ? "actor selected" : "actor"} // no itx
      style={{
        minWidth: "70px",
        backgroundColor: isSupply ? "var( --backgroundColorSupply)" : undefined, // no itx
      }}
      ref={actorElement}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onClick={(ev) => {
        if (print == undefined) {
          ev.stopPropagation();
          if (actor.selected) {
            state.selectionManager.selectedObject = undefined;
          } else {
            state.selectionManager.selectedObject = actor;
          }
          actor.updateConnectionEndpoints();
        }
      }}
      onContextMenu={onContextMenu}
      onDoubleClick={() => {
        actor.treeViewModel.handleOpen();
      }}
    >
      <ActorHeader {...props} />
      {!isSupply && <div className="channels">{children}</div>}
      {isSupply && (
        <div
          style={{
            position: "absolute",
            height: "1px",
            width: fixed ? "25px" : "0px",
            backgroundColor: "var(--connectionColorChannel)",
            left: "100%",
            top: "50%",
          }}
        >
          <div
            className="portR"
            style={{ position: "absolute", top: "1px", left: "100%", width: "0px", height: "0px" }}
          ></div>
        </div>
      )}
    </div>
  );
}
